import { addToast, Button } from '@octano/global-ui';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Col, Row } from 'reactstrap';
import { requestSendRegistration } from '../../../api/requests/courseRegistration';
import { useCourseSelectionState } from '../../../hooks/useCourseSelectionState';
import { SectionsErrors } from '../../../utils/parseErrors';
import ModalConfirmRegistration, {
  ModalConfirmRegistrationMethods,
} from './ModalConfirmRegistration';
import ModalRegistrationErrors, {
  EliminatedSection,
  ModalRegistrationMethodsErrors,
  REGISTRATION_ERROR,
} from './ModalRegistrationErrors';
import TuitionNotFoundModal, {
  TuitionErrors,
  TuitionNotFoundModalRef,
} from './TuitionNotFoundModal';

type SubmitRegistrationProps = {
  isDraft?: boolean;
};

const FinishRegistration = ({ helpEmail }: { helpEmail: string }) => {
  const confirmRegistrationRef = useRef<ModalConfirmRegistrationMethods>(null);
  const registrationErrorsRef = useRef<ModalRegistrationMethodsErrors>(null);
  const tuitionNotFountModalRef = useRef<TuitionNotFoundModalRef>(null);

  const prefix = 'finishCourseRegistration';
  const { t } = useTranslation();
  const history = useHistory();
  const {
    removeAllItems,
    selectedSections,
    resetCourseSelection,
    removeSection,
    setCourseState,
    selectedCareer,
    setErrorState,
  } = useCourseSelectionState();

  const [isFinishing, setIsFinishing] = useState<boolean>(false);

  /**
   * Despliega mensaje de error de secciones sin cupo
   * y las elimina de la selección
   */
  const errorSectionsWithoutQuota = useCallback(
    (sections: EliminatedSection[]) => {
      registrationErrorsRef?.current?.open({
        error: REGISTRATION_ERROR.QUOTA,
        eliminatedSections: sections,
      });

      sections.forEach((section) => {
        removeSection(section.id);
      });
      setCourseState(null);
    },
    [removeSection, setCourseState],
  );

  const submitRegistration = useCallback(
    async (ctx: SubmitRegistrationProps) => {
      const creditsErrors = [
        REGISTRATION_ERROR.MIN,
        REGISTRATION_ERROR.MAX,
        REGISTRATION_ERROR.CONTRACTED_REQUIRED,
      ];
      if (isFinishing) {
        return;
      }
      setIsFinishing(true);

      try {
        if (selectedCareer) {
          const { error } = await requestSendRegistration({
            isDraft: ctx?.isDraft,
            studyPlanEnrollmentId: selectedCareer.studyPlanEnrollmentId,
            mentionId: selectedCareer.mention?.id || null,
            enrollments: selectedSections.map((section) => {
              return {
                sectionId: section.id,
                semesterCourseId: section.course.semesterCourse!,
              };
            }),
          });

          if (error) {
            if (error.data?.sections) {
              errorSectionsWithoutQuota(error.data.sections);
              return;
            }
            if (
              error.data?.tuition === TuitionErrors.CURRENT_TUITION_NOT_FOUND
            ) {
              tuitionNotFountModalRef.current?.openModal();
              return;
            }
            if (creditsErrors.includes(error?.data?.credits)) {
              registrationErrorsRef?.current?.open({
                error: error?.data?.credits,
              });
              return;
            }
            if (error.data?.code === SectionsErrors.CONFLICT_SECTION_SCHEDULE) {
              setErrorState(error.data);
              return;
            }
            if (error) {
              throw new Error(REGISTRATION_ERROR.CONNECTION);
            }
          }

          addToast({
            icon: 'success',
            color: 'success',
            text: t(`${prefix}.draftSaved`),
          });

          if (!ctx?.isDraft) {
            resetCourseSelection();
            history.replace(
              `/enrollment/${selectedCareer.studyPlanEnrollmentId}/detail`,
            );
          }
        }
      } catch (error) {
        registrationErrorsRef?.current?.open({
          error: REGISTRATION_ERROR.CONNECTION,
        });
      } finally {
        confirmRegistrationRef?.current?.close();
        setIsFinishing(false);
      }
    },
    [
      t,
      setErrorState,
      resetCourseSelection,
      isFinishing,
      history,
      selectedSections,
      selectedCareer,
      errorSectionsWithoutQuota,
    ],
  );

  return (
    <>
      {/* BOTONES PARA BORRAR TODA LA SELECCIÓN O CONFIRMAR LA INSCRIPCIÓN DE ASIGNATURAS */}
      <Row className="py-3">
        <Col xs={12} lg={4} className="py-1">
          <Button
            text={t(`${prefix}.btnEraseAllItems`)}
            onClick={removeAllItems}
            outlined
            disabled={isFinishing}
            color="danger"
            fullwidth
          />
        </Col>
        <Col xs={12} lg={4} className="py-1">
          <Button
            text={t(`${prefix}.btnDraft`)}
            onClick={() => submitRegistration({ isDraft: true })}
            loading={isFinishing}
            outlined
            fullwidth
          />
        </Col>
        <Col xs={12} lg={4} className="py-1">
          <Button
            text={t(`common.btnNext`)}
            onClick={() => confirmRegistrationRef?.current?.open({})}
            loading={isFinishing}
            fullwidth
          />
        </Col>
      </Row>

      {/* MODAL PARA INDICAR ERRORES EN LA INSCRIPCIÓN DE ASIGNATURAS */}
      <ModalRegistrationErrors
        ref={registrationErrorsRef}
        helpEmail={helpEmail}
      />

      <ModalConfirmRegistration
        ref={confirmRegistrationRef}
        onConfirm={() => submitRegistration({ isDraft: false })}
      />
      <TuitionNotFoundModal ref={tuitionNotFountModalRef} />
    </>
  );
};

export default FinishRegistration;
